import { Auth0Client, GenericError } from "@auth0/auth0-spa-js";
import { getAuthConfig } from "@Config/auth";
import { gatewayUrl } from "@Constants/gatewayUrl";
import axios, { isAxiosError } from "axios";
import { createOfficeSession, revokeGatewaySession } from "./officeSession";
import { AuthError, sendWithGatewayToken, TokenManager } from "./tokenManager";
export const AUTH_SCOPE = "openid profile email offline_access";
/** Written by the sign-in dialog once Auth0 has stored its tokens. */ export const SIGNED_IN_KEY = "juristic.word.signedIn";
/** The pre-refresh-token sign-in kept the whole user, gateway token included, under this key. */ export const LEGACY_USER_KEY = "user";
const REFRESH_REFUSED = new Set([
    "invalid_grant",
    "missing_refresh_token",
    "login_required"
]);
export function createAuth0(config) {
    return new Auth0Client({
        domain: config.domain,
        clientId: config.clientId,
        cacheLocation: "localstorage",
        useRefreshTokens: true,
        authorizationParams: {
            redirect_uri: config.redirect_uri,
            audience: config.audience,
            scope: AUTH_SCOPE
        }
    });
}
let auth0 = null;
function auth0Client() {
    auth0 ??= getAuthConfig().then(createAuth0).catch((error)=>{
        auth0 = null;
        throw error;
    });
    return auth0;
}
async function auth0AccessToken({ force }) {
    const client = await auth0Client();
    try {
        return await client.getTokenSilently({
            cacheMode: force ? "off" : "on"
        });
    } catch (error) {
        if (error instanceof GenericError && REFRESH_REFUSED.has(error.error)) {
            throw new AuthError(error.error === "invalid_grant" ? "expired" : "notSignedIn", error.message);
        }
        throw error;
    }
}
let tokenManager = null;
export function getTokenManager() {
    tokenManager ??= new TokenManager({
        auth0AccessToken,
        clearAuth0: async ()=>(await auth0Client()).logout({
                openUrl: false
            }),
        exchange: (token)=>createOfficeSession(gatewayUrl, token),
        revoke: (token)=>revokeGatewaySession(gatewayUrl, token)
    });
    return tokenManager;
}
const isGatewayUrl = (url)=>!!url && url.startsWith(gatewayUrl);
const bearerOf = (header)=>String(header ?? "").replace(/^Bearer\s+/i, "");
/** `fetch` for gateway calls: the current token, renewed and resent once on a 401. */ export const gatewayFetch = (input, init)=>sendWithGatewayToken(getTokenManager(), (token)=>{
        const headers = new Headers(init?.headers);
        headers.set("Authorization", `Bearer ${token}`);
        return globalThis.fetch(input, {
            ...init,
            headers
        });
    });
/** Puts the current token on every authorised axios call to the gateway, whatever token the caller held. */ export function installGatewayAuth() {
    axios.interceptors.request.use(async (config)=>{
        if (!isGatewayUrl(config.url) || !config.headers.Authorization) return config;
        config.headers.Authorization = `Bearer ${await getTokenManager().getGatewayToken()}`;
        return config;
    });
    axios.interceptors.response.use(undefined, (error)=>{
        const config = isAxiosError(error) ? error.config : undefined;
        const isUnauthorized = isAxiosError(error) && error.response?.status === 401;
        if (!config || !isUnauthorized || config.authRetried || !isGatewayUrl(config.url)) {
            throw error;
        }
        getTokenManager().invalidateGatewayToken(bearerOf(config.headers.Authorization));
        return axios.request({
            ...config,
            authRetried: true
        });
    });
}
